import React, {HTMLAttributes, StrictMode} from 'react';
import ReactDOM from 'react-dom';

import * as serviceWorker from './serviceWorker';
import {Provider} from "react-redux";
import {store} from "./store";
import {ApolloProvider} from "@apollo/client";
import {xGqlMain} from "./utils/gql-client";

import XWpProvider from "./components/context/XWpProvider";

import cfg from "./config/general-config";
import './index.scss';
import './assets/css/_tailwind.build.css';
import {XAvailData} from "./types/gen-types";
import createTheme from '@material-ui/core/styles/createTheme';
import { ThemeProvider } from '@material-ui/core/styles';
import { AvailableBookings } from './views/AvailableBookings';

/**
 * NOTE: for theme-specific colors, use TailwindCSS custom colors instead or raw SCSS.
 *  Do NOT use MUI theme because it's too restrictive. May also use `styled-component`
 *  w/ custom colors set in JS file (e.g. tailwind-colors.js).
 *
 * @sources
 *  - https://blog.logrocket.com/3-ways-to-add-custom-fonts-to-your-material-ui-project/
 *  - https://material-ui.com/customization/typography/#self-hosted-fonts
 */
const xTheme = createTheme({
    typography: {
        fontFamily: [
            'Montserrat',
            'regular'
        ].join(',')
    }
});


/**
 * Sub-app container; wrapper to avoid boilerplate
 * @param props
 * @constructor
 */
const AppContainer = (props: AppContainerProps) => {
    return (
        <StrictMode>
            <ApolloProvider client={xGqlMain}>
                <Provider store={store}>
                    <ThemeProvider theme={xTheme}>
                        <XWpProvider xdata={props.xdata}>
                            {/*<ScopedCssBaseline>*/}
                            {props.children}
                            {/*</ScopedCssBaseline>*/}
                        </XWpProvider>
                    </ThemeProvider>
                </Provider>
            </ApolloProvider>
        </StrictMode>
    )
}


// const appInsertionId = 'root'
const appInsertionId = 'kab-root'
const target = document.getElementById(appInsertionId)

// todo reactivate this in PROD to make sure we always load EXPLORATION by default if nothing else
// const mode = target?.getAttribute("data-mode") || XModeType.EXPLORATION
const mediaUrl = target?.getAttribute("data-upload")
    || `${cfg.wpHomeUrl}/${cfg.wpUploadDir}/`

// extract important booking / availability data passed from WordPress
// 1 - date ranges for low/high seasons
const highRanges = JSON.parse(target?.dataset["high_ranges"] || "[]")
const highDays = JSON.parse(target?.dataset["high_days"] || "[]")
// 2 - IDs of WooCommerce bookable cabin products
const idStilts = JSON.parse(target?.dataset["id_stilts"] || "0")
const idCabanitas = JSON.parse(target?.dataset["id_cabanitas"] || "0")
const idKaboum = JSON.parse(target?.dataset["id_kaboum"] || "0")
// 3 - price options for all 3 cabin types
const priceStilts = JSON.parse(target?.dataset["price_stilts"] || "{}")
const priceCabanitas = JSON.parse(target?.dataset["price_cabanitas"] || "{}")
const priceKaboum = JSON.parse(target?.dataset["price_kaboum"] || "{}")

// todo use custom HTML attributes of 'kab-root' to launch different apps & code based on
//  those attributes generated in PHP code (refer to DigitalOcean doc for more info)
if (target) {
    let CoreComponent: JSX.Element
    CoreComponent = <AvailableBookings/>

    ReactDOM.render(
        (
            <AppContainer xdata={{
                wpMediaUrl: mediaUrl,
                highRanges,
                highDays,
                idStilts,
                idCabanitas,
                idKaboum,
                priceStilts,
                priceCabanitas,
                priceKaboum
            }}>
                {CoreComponent}
            </AppContainer>
        ),
        target
    )
}


interface AppContainerProps extends HTMLAttributes<any> {
    xdata: XAvailData
}


// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
